import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Mail, Sparkles, BellRing, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const NewsletterCTA = () => {
  const [email, setEmail] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gdpr) { toast.error("Debes aceptar la política de privacidad"); return; }
    setLoading(true);
    const { error } = await supabase.from("newsletter_subscribers").insert({
      email: email.trim().toLowerCase(),
      gdpr_consent: true,
      gdpr_consent_at: new Date().toISOString(),
    });
    setLoading(false);
    if (error) {
      if (error.code === "23505") toast.info("Ya estás suscrito");
      else toast.error("Error al suscribirte");
    } else {
      toast.success("¡Suscrito! Revisa tu correo.");
      setEmail("");
      setGdpr(false);
    }
  };

  const perks = [
    { icon: Tag, label: "Mejores ofertas" },
    { icon: BellRing, label: "Nuevos modelos" },
    { icon: Sparkles, label: "Guías y trucos" },
  ];

  return (
    <section className="container py-16">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 shadow-lg shadow-primary/5 md:p-12">
        {/* Decorative blobs */}
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary/15 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-accent/15 blur-3xl" />

        <div className="relative grid items-center gap-8 lg:grid-cols-2">
          {/* Left: copy */}
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <Mail className="h-3.5 w-3.5" /> Newsletter semanal
            </div>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
              No te pierdas las mejores <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">ofertas en patinetes</span>
            </h2>
            <p className="mt-3 max-w-md text-base text-muted-foreground">
              Recibe cada semana una selección curada de patinetes destacados, novedades y consejos. Sin spam, cancela cuando quieras.
            </p>

            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground/80">
              {perks.map(({ icon: Icon, label }) => (
                <li key={label} className="inline-flex items-center gap-1.5">
                  <Icon className="h-4 w-4 text-primary" /> {label}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form */}
          <div className="rounded-2xl border border-border bg-card/80 p-6 backdrop-blur-sm shadow-sm md:p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nl-cta-email" className="text-sm font-medium">
                  Tu email
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="nl-cta-email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 pl-10"
                  />
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Checkbox id="nl-cta-gdpr" checked={gdpr} onCheckedChange={(v) => setGdpr(!!v)} className="mt-0.5" />
                <Label htmlFor="nl-cta-gdpr" className="text-xs leading-relaxed text-muted-foreground">
                  Acepto la <a href="/privacidad" className="text-primary underline-offset-2 hover:underline">Política de Privacidad</a> y recibir comunicaciones comerciales.
                </Label>
              </div>

              <Button type="submit" size="lg" disabled={loading} className="h-12 w-full rounded-full bg-gradient-to-r from-primary to-primary/85 font-semibold shadow-md shadow-primary/20">
                {loading ? "Suscribiendo..." : "Suscribirme gratis"}
              </Button>

              <p className="text-center text-[11px] text-muted-foreground">
                Más de 2.500 suscriptores ya reciben nuestras ofertas semanales
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterCTA;
